import type {
  ClerkUser,
  PageAdminEvaluationsData,
  PageAdminUsersFilter,
} from '@agentselly/immodossier-shared';
import {
  ActionSchema,
  AppAction,
  AppRoute,
  pageAdminUsersFilterSchema,
  QueryReference,
} from '@agentselly/immodossier-shared';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useBackendQuery } from '~/helper/useBackendQuery';
import { ErrorWidget } from '~/components/widget/base/ErrorWidget';
import { LoadingWidget } from '~/components/widget/base/LoadingWidget';
import { formatDateTime } from '@agentselly/ui';
import { useTranslate } from '@tolgee/react';
import { clsx } from 'clsx';
import { useEffect, useState } from 'react';
import ReactJson from 'react-json-view';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useQueryClient } from '@tanstack/react-query';
import { fetchBackend } from '~/helper/fetchBackend.ts';
import { useTheme } from '~/context/ThemeContext';
import { useAppParams } from '~/helper/useAppParams';

export const Route = createFileRoute(AppRoute.$LANG_$COMPANYID_ADMIN_USERS)({
  component: Body,
});

function Body() {
  const { t } = useTranslate();
  const navigate = useNavigate();
  const { lang, companyId } = useAppParams();

  const rawSearch = Route.useSearch();
  const parsedSearch = pageAdminUsersFilterSchema.safeParse(rawSearch);
  const currentFilter: PageAdminUsersFilter = parsedSearch.success
    ? parsedSearch.data
    : {};

  const query = useBackendQuery(QueryReference.PageAdminUsers);

  const [
    selectedUserId,
    setSelectedUserId,
  ] = useState('');

  if (query.isLoading) {
    return <LoadingWidget />;
  }

  if (query.isError) {
    return <ErrorWidget error={query.error} />;
  }

  async function updateFilter(filter: PageAdminUsersFilter) {
    const newFilter = {
      ...currentFilter,
      ...filter,
    };

    const shape = pageAdminUsersFilterSchema._def.shape();
    const keys = Object.keys(shape) as (keyof PageAdminUsersFilter)[];

    for (const key of keys) {
      if (key === 'page') {
        continue;
      }

      const value = newFilter[key];

      if (value === currentFilter[key]) {
        continue;
      }

      if (
        typeof value === 'undefined' ||
        value === null ||
        value === '' ||
        (Array.isArray(value) && !value.length)
      ) {
        continue;
      }

      // Reset page when changing filter
      newFilter.page = 1;
      break;
    }

    const nextSearch: Record<string, unknown> = {};

    for (const key of keys) {
      const value = newFilter[key];

      if (
        typeof value === 'undefined' ||
        value === null ||
        value === '' ||
        (Array.isArray(value) && !value.length)
      ) {
        continue;
      }

      nextSearch[key] = value;
    }

    await navigate({
      to: Route.fullPath,
      params: { lang, companyId },
      search: nextSearch as unknown as PageAdminUsersFilter,
      replace: true,
    });

    await query.refetch();
  }

  const paginationProps: PaginationWidgetProps = {
    pagination: query.data?.pagination,
    resultDate: formatDateTime(query.data?.resultTimestamp),
    updateFilter: updateFilter,
  };

  if (!parsedSearch.success) {
    return <ErrorWidget error={parsedSearch.error} />;
  }

  const selectedUser = selectedUserId
    ? query.data?.users?.find((it) => it.id === selectedUserId)
    : undefined;

  return (
    <div className="v3-stack pb-8">
      {/* User Details & Edit Modal Dialog */}
      <UserDetailsDialog
        onCancel={() => setSelectedUserId('')}
        canMutate={query.data?.canMutate}
        user={selectedUser}
      />

      {/* Header */}
      <div className="v3-head">
        <h1>{t('admin_users_title', 'Benutzerverwaltung')}</h1>
        <p>
          {t(
            'admin_users_subtitle',
            'Clerk Benutzerkonten, Rollen und Berechtigungen',
          )}
        </p>
      </div>

      {/* Filter Card */}
      <div className="v3-card">
        <h2 className="text-base font-bold text-[var(--v3-tx)]">
          {t('admin_users_filter_title', 'Filter & Sortierung')}
        </h2>

        <div className="v3-fil-grid">
          <div>
            <label
              className="block text-xs font-semibold text-[var(--v3-mu)] mb-1"
              htmlFor="filterOrderBy"
            >
              {t('admin_users_sort_by', 'Sortieren nach')}
            </label>
            <select
              id="filterOrderBy"
              className="v3-inp"
              onChange={(event) =>
                updateFilter({
                  orderBy: (event.target.value ||
                    undefined) as PageAdminUsersFilter['orderBy'],
                })
              }
              defaultValue={currentFilter.orderBy || ''}
            >
              <option value="">{t('common_default', 'Standard')}</option>
              <option value="created_at">Erstellt am</option>
              <option value="updated_at">Aktualisiert am</option>
              <option value="email_address">E-Mail</option>
              <option value="first_name">Vorname</option>
              <option value="last_name">Nachname</option>
              <option value="phone_number">Telefon</option>
              <option value="username">Benutzername</option>
              <option value="last_active_at">Zuletzt aktiv</option>
              <option value="last_sign_in_at">Zuletzt angemeldet</option>
            </select>
          </div>

          <div>
            <label
              className="block text-xs font-semibold text-[var(--v3-mu)] mb-1"
              htmlFor="filterQuery"
            >
              {t('admin_users_search', 'Suche nach Name, E-Mail')}
            </label>
            <input
              id="filterQuery"
              type="text"
              className="v3-inp"
              placeholder={t('admin_users_search_placeholder', 'Suchen...')}
              onChange={(event) =>
                updateFilter({
                  query: event.target.value,
                })
              }
              defaultValue={currentFilter.query}
            />
          </div>

          <div className="flex items-center pt-5">
            <label
              className="v3-ckb"
              htmlFor="filterOrderByAscending"
            >
              <input
                id="filterOrderByAscending"
                type="checkbox"
                onChange={(event) =>
                  updateFilter({
                    orderByAscending: event.target.checked,
                  })
                }
                defaultChecked={currentFilter.orderByAscending}
              />
              <span>{t('admin_users_asc', 'Aufsteigend sortieren')}</span>
            </label>
          </div>
        </div>
      </div>

      <PaginationWidget {...paginationProps} />

      {/* User List */}
      <div className="v3-stack">
        <h2 className="text-base font-bold text-[var(--v3-tx)]">
          {t('admin_users_list_title', 'Benutzer')}
        </h2>

        {query.data?.users?.length === 0 ? (
          <div className="v3-card text-center py-8 text-[var(--v3-mu)]">
            {t('admin_users_empty', 'Keine Benutzer gefunden.')}
          </div>
        ) : null}

        <div className="flex flex-col gap-3">
          {query.data?.users?.filter(Boolean)?.map((user) => {
            const role = String(user.publicMetadata?.role || 'user');
            const roleChipClass =
              role === 'admin'
                ? 'c-bad'
                : role === 'internal'
                ? 'c-warn'
                : role === 'partner'
                ? 'c-info'
                : 'c-neu';

            const primaryEmail =
              user.emailAddresses?.find(
                (e) => e.id === user.primaryEmailAddressId,
              )?.emailAddress ||
              user.emailAddresses?.[0]?.emailAddress ||
              '–';

            return (
              <div
                key={user.id}
                onClick={() => setSelectedUserId(user.id)}
                className="v3-card cursor-pointer hover:border-[var(--v3-pri)] transition-all flex-col sm:flex-row sm:items-center justify-between gap-4 p-4"
              >
                {/* User info & avatar */}
                <div className="flex items-center gap-3.5 min-w-0 flex-1">
                  {user.imageUrl ? (
                    <img
                      alt=""
                      src={user.imageUrl}
                      className="w-11 h-11 rounded-full object-cover border border-[var(--v3-bd)] bg-[var(--v3-neubg)] flex-none"
                    />
                  ) : (
                    <div className="v3-av4 flex-none">
                      {(
                        user.firstName?.[0] ||
                        primaryEmail[0] ||
                        'U'
                      ).toUpperCase()}
                    </div>
                  )}

                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap mb-0.5">
                      <span className="font-semibold text-sm text-[var(--v3-tx)] truncate">
                        {[
                          user.firstName,
                          user.lastName,
                        ]
                          .filter(Boolean)
                          .join(' ') ||
                          user.username ||
                          primaryEmail}
                      </span>

                      <span className={clsx('v3-chip', roleChipClass)}>
                        {role}
                      </span>

                      {user.banned ? (
                        <span className="v3-chip c-bad">Gesperrt</span>
                      ) : user.locked ? (
                        <span className="v3-chip c-warn">Blockiert</span>
                      ) : (
                        <span className="v3-chip c-ok">Aktiv</span>
                      )}
                    </div>

                    <div className="text-xs text-[var(--v3-mu)] truncate">
                      {primaryEmail}
                      {user.username ? ` · @${user.username}` : ''}
                    </div>
                  </div>
                </div>

                {/* Timestamps & Details trigger */}
                <div className="flex items-center justify-between sm:justify-end gap-6 text-xs text-[var(--v3-mu)] flex-none pt-2 sm:pt-0 border-t sm:border-t-0 border-[var(--v3-bd)]">
                  <div className="text-left sm:text-right">
                    <div>
                      {user.lastActiveAt ? (
                        <>
                          Aktiv:{' '}
                          <strong className="text-[var(--v3-tx)]">
                            {formatDateTime(user.lastActiveAt)}
                          </strong>
                        </>
                      ) : (
                        <>
                          Erstellt:{' '}
                          <strong className="text-[var(--v3-tx)]">
                            {formatDateTime(user.createdAt)}
                          </strong>
                        </>
                      )}
                    </div>
                    <div className="font-mono text-[11px]">
                      ID: {user.id.slice(0, 14)}…
                    </div>
                  </div>

                  <button
                    type="button"
                    className="v3-btn sm text-xs"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedUserId(user.id);
                    }}
                  >
                    Verwalten →
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <PaginationWidget {...paginationProps} />
    </div>
  );
}

function PaginationWidget(props: PaginationWidgetProps) {
  const { t } = useTranslate();

  const page = props.pagination?.page || 0;
  const pageCount = props.pagination?.pageCount || 0;

  return (
    <div className="v3-card py-3">
      <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <PaginationButton
            disabled={page <= 1}
            updateFilter={props.updateFilter}
            targetPage={1}
            text={t('admin_evaluations_pagination_first', '« Erste')}
          />
          <PaginationButton
            disabled={page - 1 < 1}
            updateFilter={props.updateFilter}
            targetPage={page - 1}
            text={t('admin_evaluations_pagination_previous', '‹ Zurück')}
          />
        </div>

        <p className="text-center text-xs text-[var(--v3-mu)]">
          <strong className="text-[var(--v3-tx)]">
            {t('admin_evaluations_pagination_text', {
              page: page,
              total: pageCount,
            })}
          </strong>
          <br />
          {t('admin_evaluations_pagination_results', {
            resultCount: props.pagination?.total || 0,
            resultDate: props.resultDate,
          })}
        </p>

        <div className="flex items-center gap-2">
          <PaginationButton
            disabled={page + 1 > pageCount}
            updateFilter={props.updateFilter}
            targetPage={page + 1}
            text={t('admin_evaluations_pagination_next', 'Vor ›')}
          />
          <PaginationButton
            disabled={page >= pageCount}
            updateFilter={props.updateFilter}
            targetPage={pageCount}
            text={t('admin_evaluations_pagination_last', 'Letzte »')}
          />
        </div>
      </div>
    </div>
  );
}

interface PaginationWidgetProps {
  readonly pagination: PageAdminEvaluationsData['pagination'] | undefined;
  readonly resultDate: string;
  readonly updateFilter: (filter: PageAdminUsersFilter) => void;
}

function PaginationButton(props: PaginationButtonProps) {
  return (
    <button
      onClick={() =>
        props.updateFilter({
          page: props.targetPage || undefined,
        })
      }
      disabled={props.disabled}
      className="v3-btn sm text-xs"
    >
      {props.text}
    </button>
  );
}

interface PaginationButtonProps {
  readonly disabled: boolean;
  readonly updateFilter: (filter: PageAdminUsersFilter) => void;
  readonly targetPage: number;
  readonly text: string;
}

function UserDetailsDialog(props: UserDetailsDialogProps) {
  const { t } = useTranslate();
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';

  const user = props.user;

  if (!user) {
    return null;
  }

  const primaryEmail =
    user.emailAddresses?.find((it) => it.id === user.primaryEmailAddressId)
      ?.emailAddress || user.id;

  return (
    <div
      className="v3-modal-backdrop"
      onClick={() => props.onCancel()}
    >
      <div
        className="v3-modal max-w-2xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-start gap-4 pb-3 border-b border-[var(--v3-bd)]">
          <div className="min-w-0">
            <h2 className="v3-modal-title truncate">
              {t('admin_users_detail_title', 'Benutzer:')} {primaryEmail}
            </h2>
            <p className="text-xs text-[var(--v3-mu)] font-mono">{user.id}</p>
          </div>
          <button
            onClick={() => props.onCancel()}
            className="p-1 rounded hover:bg-[var(--v3-neubg)] text-[var(--v3-mu)]"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        {/* External links */}
        <div className="flex flex-wrap gap-2 text-xs">
          <a
            href={`https://dashboard.clerk.com/apps/app_2qxN29A6IozvGlwUx1mNAjiFtH0/instances/ins_2siVGQt4cvjzblObK1CneZ6KatG/users/${user.id}`}
            target="_blank"
            rel="noreferrer"
            className="v3-btn sm text-xs"
          >
            Clerk Dashboard ↗
          </a>
          {user.publicMetadata?.hubspotContactId ? (
            <a
              href={`https://app-eu1.hubspot.com/contacts/26776524/record/0-1/${user.publicMetadata?.hubspotContactId}/`}
              target="_blank"
              rel="noreferrer"
              className="v3-btn sm text-xs"
            >
              HubSpot Contact #{user.publicMetadata.hubspotContactId} ↗
            </a>
          ) : null}
        </div>

        {/* Mutations section */}
        <div className="p-4 rounded-lg bg-[var(--v3-neubg)] border border-[var(--v3-bd)]">
          <h3 className="text-sm font-bold text-[var(--v3-tx)] mb-3">
            {t('admin_users_mutation_title', 'Benutzer-Mutationen')}
          </h3>
          {props.canMutate ? (
            <UserMutationsForm user={props.user} />
          ) : (
            <p className="text-xs text-[var(--v3-mu)]">
              {t(
                'admin_evaluations_userdetail_noaccess',
                'Keine Schreibberechtigung für Mutationen.',
              )}
            </p>
          )}
        </div>

        {/* Activities and Emails */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs">
          <div className="space-y-1">
            <h4 className="font-semibold text-[var(--v3-tx)]">
              E-Mail Adressen
            </h4>
            <ul className="space-y-1 text-[var(--v3-mu)]">
              {user.emailAddresses?.map((email) => (
                <li
                  key={email.id}
                  className="flex items-center gap-1.5 flex-wrap"
                >
                  <span
                    className={clsx(
                      user.primaryEmailAddressId === email.id &&
                        'font-bold text-[var(--v3-tx)]',
                    )}
                  >
                    {email.emailAddress}
                  </span>
                  <span
                    className={clsx(
                      'v3-chip text-[10px]',
                      email.verification?.status === 'verified'
                        ? 'c-ok'
                        : 'c-warn',
                    )}
                  >
                    {email.verification?.status || 'unverified'}
                  </span>
                </li>
              ))}
            </ul>
          </div>

          <div className="space-y-1">
            <h4 className="font-semibold text-[var(--v3-tx)]">Aktivitäten</h4>
            <dl className="space-y-1 text-[var(--v3-mu)]">
              <div>
                <dt className="inline text-[var(--v3-tx)]">Erstellt: </dt>
                <dd className="inline">{formatDateTime(user.createdAt)}</dd>
              </div>
              <div>
                <dt className="inline text-[var(--v3-tx)]">Aktualisiert: </dt>
                <dd className="inline">{formatDateTime(user.updatedAt)}</dd>
              </div>
              <div>
                <dt className="inline text-[var(--v3-tx)]">Zuletzt aktiv: </dt>
                <dd className="inline">{formatDateTime(user.lastActiveAt)}</dd>
              </div>
              <div>
                <dt className="inline text-[var(--v3-tx)]">
                  Zuletzt angemeldet:{' '}
                </dt>
                <dd className="inline">{formatDateTime(user.lastSignInAt)}</dd>
              </div>
            </dl>
          </div>
        </div>

        {/* Raw JSON Accordion */}
        <details className="v3-acc mt-2">
          <summary>
            <span>DEBUG / Raw Clerk User Data</span>
            <span className="v3-chev transition-transform">▼</span>
          </summary>
          <div className="v3-acc-in">
            <ReactJson
              src={user}
              collapsed={true}
              name={false}
              theme={isDark ? 'monokai' : 'rjv-default'}
              style={{
                backgroundColor: 'transparent',
                fontSize: '12px',
              }}
            />
          </div>
        </details>
      </div>
    </div>
  );
}

interface UserDetailsDialogProps {
  readonly onCancel: () => void;
  readonly canMutate?: boolean;
  readonly user?: ClerkUser | null;
}

function UserMutationsForm(props: UserMutationsFormProps) {
  const metadata = props.user.publicMetadata || {};
  const queryClient = useQueryClient();

  const initialRole = String(metadata.role || 'user');

  const [
    isLoading,
    setLoading,
  ] = useState(false);
  const [
    error,
    setError,
  ] = useState<unknown>(null);
  const [
    success,
    setSuccess,
  ] = useState('');
  const [
    role,
    setRole,
  ] = useState(initialRole);
  const [
    hubspotContactId,
    setHubspotContactId,
  ] = useState(
    metadata.hubspotContactId ? String(metadata.hubspotContactId) : '',
  );
  const [
    banned,
    setBanned,
  ] = useState<boolean>(metadata.banned || false);
  const [
    locked,
    setLocked,
  ] = useState<boolean>(metadata.locked || false);

  // Mandatory role change confirmation dialog state
  const [
    showRoleConfirmModal,
    setShowRoleConfirmModal,
  ] = useState(false);

  useEffect(() => {
    if (!error && !success) {
      return;
    }
    setLoading(false);
  }, [
    error,
    success,
  ]);

  async function executeSave() {
    try {
      setLoading(true);
      setError(null);
      setSuccess('');

      const rawBody = {
        userId: props.user.id,
        role: role,
        hubspotContactId: hubspotContactId
          ? parseInt(hubspotContactId, 10)
          : undefined,
        banned: Boolean(banned),
        locked: Boolean(locked),
      };

      const body = ActionSchema[AppAction.UserMutation].safeParse(rawBody);

      if (!body.success) {
        throw body.error;
      }

      const response = await fetchBackend(`/action/${AppAction.UserMutation}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body.data),
      });

      if (!response.ok) {
        throw new Error(`${response.status} ${response.statusText}`);
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(JSON.stringify(result.errors));
      }

      setSuccess(
        `Benutzer am ${formatDateTime(new Date())} erfolgreich angepasst.`,
      );

      await queryClient.invalidateQueries({
        queryKey: [QueryReference.PageAdminUsers],
      });
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
      setShowRoleConfirmModal(false);
    }
  }

  function handleSaveClick() {
    // Check if role has changed - mandatory confirmation modal
    if (role !== initialRole) {
      setShowRoleConfirmModal(true);
    } else {
      void executeSave();
    }
  }

  if (isLoading) {
    return <LoadingWidget />;
  }

  return (
    <div className="flex flex-col gap-4">
      {/* Role Change Confirmation Modal */}
      {showRoleConfirmModal ? (
        <div
          className="v3-modal-backdrop z-[9999]"
          onClick={() => setShowRoleConfirmModal(false)}
        >
          <div
            className="v3-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="v3-modal-title text-[var(--v3-warn)]">
              Rollenänderung bestätigen
            </h3>
            <p className="v3-modal-desc">
              Möchten Sie die Rolle für diesen Benutzer wirklich von{' '}
              <strong className="text-[var(--v3-tx)] font-semibold">
                {initialRole}
              </strong>{' '}
              auf{' '}
              <strong className="text-[var(--v3-tx)] font-semibold">
                {role}
              </strong>{' '}
              ändern?
            </p>
            <p className="text-xs text-[var(--v3-mu)]">
              Diese Änderung wirkt sich unmittelbar auf Zugriffsrechte und
              Autorisierungen des Nutzers aus.
            </p>
            <div className="v3-modal-actions">
              <button
                type="button"
                className="v3-btn sm"
                onClick={() => setShowRoleConfirmModal(false)}
              >
                Abbrechen
              </button>
              <button
                type="button"
                className="v3-btn sm pri"
                onClick={() => void executeSave()}
              >
                Bestätigen & Speichern
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 items-end">
        <div>
          <label
            className="block text-xs font-semibold text-[var(--v3-mu)] mb-1"
            htmlFor="mutateRole"
          >
            Rolle
          </label>
          <select
            id="mutateRole"
            className="v3-inp"
            value={role}
            onChange={(event) => setRole(event.target.value)}
          >
            <option value="user">User</option>
            <option value="partner">Partner (ACHTUNG)</option>
            <option value="internal">Internal (ACHTUNG)</option>
            <option value="admin">Admin (ACHTUNG)</option>
          </select>
        </div>

        <div>
          <label
            className="block text-xs font-semibold text-[var(--v3-mu)] mb-1"
            htmlFor="mutateHubspot"
          >
            HubSpot Contact ID
          </label>
          <input
            id="mutateHubspot"
            type="number"
            className="v3-inp"
            value={hubspotContactId}
            onChange={(event) => setHubspotContactId(event.target.value)}
          />
        </div>

        <div className="flex items-center gap-4">
          <label className="v3-ckb">
            <input
              type="checkbox"
              checked={banned}
              onChange={(event) => setBanned(event.target.checked)}
            />
            <span>Banned</span>
          </label>

          <label className="v3-ckb">
            <input
              type="checkbox"
              checked={locked}
              onChange={(event) => setLocked(event.target.checked)}
            />
            <span>Locked</span>
          </label>
        </div>

        <div className="flex justify-end">
          <button
            type="button"
            className="v3-btn pri sm w-full sm:w-auto"
            onClick={handleSaveClick}
          >
            Speichern
          </button>
        </div>
      </div>

      {error ? (
        <div className="v3-callout warn text-xs">
          {error instanceof Error ? error.message : String(error)}
        </div>
      ) : null}

      {success ? <div className="v3-callout text-xs">{success}</div> : null}
    </div>
  );
}

interface UserMutationsFormProps {
  readonly user: ClerkUser;
}
